// Leaflet map for the /pt page's Map view - clinic pins colored by Keep/Cut/Undecided plus
// three fixed reference pins (home, school, boathouse). Modeled on the Sites tab's Map view in
// ChlorKing's FieldService.html: stock OSM raster tiles (free, no key), Leaflet only, no
// react-leaflet wrapper. Unlike that page this map is mounted only while the view is active (no
// display:none dance / invalidateSize needed) since ~20 pins in one metro area never need
// clustering the way ~190 nationwide sites do.
import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Clinic, Place } from "../lib/types";

const STATUS_COLOR: Record<string, string> = {
  keep: "#16a34a",
  cut: "#dc2626",
  unranked: "#7c3aed", // matches the violet claude_rank badge on the clinic cards
};

const PLACE_ICON: Record<Place["kind"], { glyph: string; color: string }> = {
  home: { glyph: "\u{1F3E0}", color: "#2563eb" },
  school: { glyph: "\u{1F393}", color: "#4f46e5" },
  boathouse: { glyph: "\u{1F6A3}", color: "#0d9488" },
};

function placeDivIcon(kind: Place["kind"]) {
  const { glyph, color } = PLACE_ICON[kind];
  return L.divIcon({
    className: "",
    html: `<div style="
      display:flex;align-items:center;justify-content:center;
      width:30px;height:30px;border-radius:50%;
      background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.4);
      font-size:15px;line-height:1;
    ">${glyph}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

export function ClinicMap({
  clinics,
  places,
  onSelectClinic,
  focus,
}: {
  clinics: Clinic[];
  places: Place[];
  /** Clicking a clinic's dot opens the same detail/edit view the List's card links to. */
  onSelectClinic: (id: string) => void;
  /** Set by the card's "Show on map" button - zooms/pans to a clinic's environs on arrival. */
  focus?: { lat: number; lon: number; ts: number } | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  // Doubles as "the map has an established view" - set either by the initial fitBounds or by
  // the first focus request, whichever happens first (see the two effects below).
  const hasFitRef = useRef(false);
  const lastFocusTsRef = useRef<number | null>(null);

  // Init once per mount (the component itself is only mounted while Map view is showing).
  useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    // Container was just inserted; Leaflet needs a settled layout before it measures itself.
    setTimeout(() => map.invalidateSize(), 0);
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // (Re)draw markers whenever the filtered clinic list or the fixed places change.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    const points: L.LatLng[] = [];

    places.forEach((p) => {
      const marker = L.marker([p.lat, p.lon], { icon: placeDivIcon(p.kind) });
      marker.bindTooltip(p.name, { direction: "top" });
      marker.bindPopup(
        `<strong>${escapeHtml(p.name)}</strong><br>${escapeHtml(p.address)}, ${escapeHtml(p.city)} ${escapeHtml(p.zip)}`,
      );
      layer.addLayer(marker);
      points.push(L.latLng(p.lat, p.lon));
    });

    clinics.forEach((c) => {
      const color = STATUS_COLOR[c.evaluation.status] || STATUS_COLOR.unranked;
      const marker = L.circleMarker([c.lat, c.lon], {
        radius: 9,
        color: "white",
        weight: 2,
        fillColor: color,
        fillOpacity: 0.9,
      });
      marker.bindTooltip(c.name, { direction: "top" });
      // Click opens the same card/modal the List view uses (Keep/Cut + notes included) rather
      // than a separate popup - one place to see or edit a clinic, regardless of which view found it.
      marker.on("click", () => onSelectClinic(c.id));
      layer.addLayer(marker);
      points.push(L.latLng(c.lat, c.lon));
    });

    // Only fit bounds the first time there's something to show - a later filter change (e.g.
    // switching to the Keep chip) re-draws markers but shouldn't yank the user's own pan/zoom.
    // Skipped when a focus request is pending (below) - that request owns the initial view.
    if (!hasFitRef.current && points.length > 0 && !focus) {
      map.fitBounds(L.latLngBounds(points), { padding: [30, 30] });
      hasFitRef.current = true;
    }
  }, [clinics, places, onSelectClinic, focus]);

  // A "Show on map" click, on a card or in the marker modal - zoom to that clinic's environs.
  // Runs after the map-init effect above (mapRef is already set), keyed off `ts` so re-locating
  // the same clinic twice in a row still fires. flyTo animates; setView is used only for the
  // very first view the map ever gets (Leaflet needs a view before it can animate to a new one).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus || lastFocusTsRef.current === focus.ts) return;
    lastFocusTsRef.current = focus.ts;
    if (hasFitRef.current) {
      map.flyTo([focus.lat, focus.lon], 15);
    } else {
      map.setView([focus.lat, focus.lon], 15);
      hasFitRef.current = true;
    }
  }, [focus]);

  return <div ref={containerRef} className="h-[70vh] w-full rounded-lg border border-slate-200" />;
}

function escapeHtml(s: string): string {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}
